import { Company } from "@finway-group/shared/lib/models"
import { Button, Collapse, Divider, List, PageHeader } from "antd"
import CompanyListSort from "Components/companyListSort"
import CompanySettingsModal from "Components/modals/companySettings.modal"
import SearchInput from "Components/searchInput"
import SelectedCompanyPreview from "Components/selectedCompanyPreview"
import React, { useEffect, useMemo, useState } from "react"
import { Plus as PlusIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { useModal } from "Shared/context/modal.context"
import { useCompanies, useCompany } from "Shared/hooks/company.hooks"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import { NotificationService } from "Shared/services"
import { markCompanyAsDeleted, updateCompany } from "Shared/store/actions/company/companyActions"
import { searchCompanies, sortCompaniesByCreatedAt, sortCompaniesByName, sortCompanyByName } from "Shared/utils/company.utils"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { useDebouncedCallback } from "use-debounce/lib"
import CompanyCard from "./CompanyCard"

export enum SortEnum {
    ASC = "asc",
    DESC = "desc",
}

export enum SortCompanyEnum {
    NAME = "name",
    DATE = "dateCreated",
}

const { Panel } = Collapse

const CompanyList = () => {
    const { t } = useTranslation()
    const { showModal } = useModal()
    const dispatch = useDispatch()
    const history = useHistory()

    const [showDeleted, setShowDeleted] = useStateIfMounted(false)
    const { name, logo, _id, isRoot } = useCompany()

    const companies = useCompanies(true)

    const allActiveCompanies = useMemo(() => companies.filter((company) => !company.deleted && company._id !== _id), [companies]).sort(sortCompanyByName)
    const deletedCompanies = useMemo(() => companies.filter(({ deleted }) => !!deleted), [companies]).sort(sortCompanyByName)

    const employee = useLoggedInEmployeeProfile()
    const isAdmin = employee.activeCompanyProfile.isAdmin

    const openCompanyModal = () => showModal(CompanySettingsModal, false, { company: {}, isShowing: true, isEditMode: false })
    const handleOnCompanyEdit = (company: Company) => showModal(CompanySettingsModal, false, { company, isShowing: true, isEditMode: true })

    const handleOnDelete = async (id: string) => {
        try {
            await markCompanyAsDeleted(id)(dispatch)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        }
    }

    const handleOnRestore = async (company: Company) => {
        try {
            await updateCompany({ ...company, deleted: false })(dispatch)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        }
    }

    const extrasButton = () => {
        const action = []

        if (isAdmin) {
            action.push(
                <Button key="randomKey" className="mb-0 self-center" onClick={openCompanyModal}>
                    <PlusIcon />
                </Button>
            )
        }

        return action
    }

    useEffect(() => {
        !deletedCompanies.length && showDeleted && setShowDeleted(false)
    }, [deletedCompanies.length])

    const [activeCompanyList, setActiveCompanyList] = useState(allActiveCompanies)
    const [searchValue, setSearchValue] = useState("")
    const [sort, setSort] = useState({
        direction: SortEnum.DESC,
        field: SortCompanyEnum.DATE,
    })

    useEffect(() => {
        setActiveCompanyList(searchCompanies(searchValue, allActiveCompanies))
    }, [searchValue, allActiveCompanies])

    useEffect(() => {
        let list: Company[] = []

        if (sort.field === SortCompanyEnum.DATE) list = sortCompaniesByCreatedAt(allActiveCompanies, sort.direction)
        if (sort.field === SortCompanyEnum.NAME) list = sortCompaniesByName(allActiveCompanies, sort.direction)

        setActiveCompanyList(list)
    }, [sort])

    const searchCallback = useDebouncedCallback(async (searchString: string) => {
        setSearchValue(searchString)
    }, 800)

    return (
        <div className="w-full">
            <div className="md:hidden">
                <PageHeader
                    ghost={false}
                    onBack={() => history.push("/settings")}
                    title={<SelectedCompanyPreview name={name} logo={logo} isRoot={isRoot} />}
                    extra={extrasButton()}
                />
            </div>

            <div className="justify-between xs:items-center hidden md:flex">
                <h2 className="mb-0">{t("label:companies")}</h2>
                <div className="flex gap-4">
                    {allActiveCompanies.length > 1 && (
                        <>
                            <SearchInput onSearch={(value) => searchCallback.callback(value)} value={searchValue} />
                            <CompanyListSort onClick={(direction, field) => setSort({ direction, field })} currentSort={sort} />
                        </>
                    )}

                    {isAdmin && (
                        <Button className="hidden md:block mb-0" onClick={openCompanyModal}>
                            <PlusIcon /> {t("action:company_settings.add_new")}
                        </Button>
                    )}
                </div>
            </div>

            <div className="pt-10 pb-30 hidden md:flex">
                <SelectedCompanyPreview name={name} logo={logo} isRoot={isRoot} />
            </div>

            <div className="flex flex-col">
                <List
                    dataSource={activeCompanyList}
                    renderItem={(company) => (
                        <CompanyCard key={company._id} company={company} loggedInUser={employee} canDelete={isAdmin} onEdit={handleOnCompanyEdit} onDelete={handleOnDelete} />
                    )}
                    pagination={{
                        position: "bottom",
                        showSizeChanger: true,
                        hideOnSinglePage: true,
                        pageSizeOptions: ["5", "10", "20", "50", "100"],
                    }}
                />

                {isAdmin && deletedCompanies.length > 0 && (
                    <>
                        <Divider />
                        <Collapse className="w-100 -ml-12 -mb-10 pb-0" expandIconPosition="right" onChange={() => setShowDeleted(!showDeleted)} ghost>
                            <Panel header={t("label:deleted")} key="1" />
                        </Collapse>
                        {showDeleted && (
                            <div className="-mt-16">
                                {deletedCompanies?.map((company: Company) => (
                                    <CompanyCard
                                        key={company._id}
                                        company={company}
                                        loggedInUser={employee}
                                        canDelete={isAdmin}
                                        onEdit={handleOnCompanyEdit}
                                        onDelete={handleOnDelete}
                                        onRestore={handleOnRestore}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default React.memo(CompanyList)
